import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import BrandName from "@/components/BrandName";
import ContextSwitcher from "@/components/ContextSwitcher";
import {
  LogOut, IdCard, Film, ShoppingCart, MapPin, Video, Store, CreditCard,
  LayoutDashboard, Settings as SettingsIcon, Search, Inbox as InboxIcon,
  Shield, BadgeCheck, Users, ArrowLeft, Lock, Briefcase, Home,
  Menu, X, ChevronDown,
} from "lucide-react";

const ASSOC_LOGO = {
  cintaa: { label: "CINTAA", color: "#1E3A8A" },
  fwice:  { label: "FWICE",  color: "#9B2C2C" },
  mptca:  { label: "MPTCA",  color: "#15803D" },
  iftda:  { label: "IFTDA",  color: "#7C3AED" },
  scwa:   { label: "SCWA",   color: "#B45309" },
  wicc:   { label: "WICC",   color: "#0E7490" },
};

const TIER_ACCENT = {
  labor:     "#8D9096",
  direction: "#D9383A",
  creative:  "#6B4EFF",
  stage:     "#EC4899",
};

const formatRole = (key) =>
  (key || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const isIndividualAccount = (user) => !!user &&
  !["production_house", "studio", "studio_admin", "studio_network",
    "advertising_agency", "event_production", "vendor", "trade_org",
    "association_admin", "admin"].includes(user.account_type);

// Nav item catalogue — split into `primary` (always visible at lg+) and
// `secondary` (tucked inside the More ▾ popover). Mobile drawer shows
// all items in one continuous list. `tid` matches the ORIGINAL flat-nav
// data-testids so existing e2e suites keep working.
function buildNavItems(user) {
  const items = [];
  if (isIndividualAccount(user)) {
    items.push({ key: "me",        to: "/me",          icon: Briefcase,       label: "My Desk",     tid: "nav-my-desk",         bucket: "primary" });
  }
  items.push(
    { key: "dash",      to: "/dashboard",       icon: LayoutDashboard, label: "Dashboard",   tid: "nav-dashboard",          bucket: "primary" },
    { key: "directory", to: "/directory",       icon: Users,           label: "Directory",   tid: "nav-directory",          bucket: "primary" },
    { key: "inbox",     to: "/inbox",           icon: InboxIcon,       label: "Inbox",       tid: "nav-inbox",              bucket: "primary" },
    { key: "icard",     to: "/icard",           icon: IdCard,          label: "iCard",       tid: "nav-icard",              bucket: "primary" },
    { key: "projects",  to: "/projects",        icon: Film,            label: "Projects",    tid: "nav-projects",           bucket: "primary" },
    // ↓ everything below moves into the "More" popover on lg+ but
    // stays in the drawer on mobile.
    { key: "orgs",      to: "/directory/orgs",  icon: Store,           label: "Orgs",        tid: "nav-directory-orgs",     bucket: "secondary" },
    { key: "discover",  to: "/discover",        icon: Search,          label: "Discover",    tid: "nav-discover",           bucket: "secondary" },
    { key: "proc",      to: "/procurement",     icon: ShoppingCart,    label: "Procurement", tid: "nav-procurement",        bucket: "secondary" },
    { key: "att",       to: "/attendance",      icon: MapPin,          label: "Attendance",  tid: "nav-attendance",         bucket: "secondary" },
    { key: "aud",       to: "/auditions",       icon: Video,           label: "Auditions",   tid: "nav-auditions",          bucket: "secondary" },
  );
  if (user?.account_type === "vendor") {
    items.push({ key: "vendor",   to: "/vendor",         icon: Store,  label: "Vendor",         tid: "nav-vendor",         bucket: "secondary" });
  }
  if (user?.account_type === "production_house") {
    items.push({ key: "override", to: "/grant-override", icon: Shield, label: "Grant Override", tid: "nav-grant-override", bucket: "secondary" });
  }
  items.push(
    { key: "pricing",  to: "/pricing",  icon: CreditCard,   label: "Pricing",  tid: "nav-pricing",  bucket: "secondary" },
    { key: "privacy",  to: "/privacy",  icon: Lock,         label: "Privacy",  tid: "nav-privacy",  bucket: "secondary" },
    { key: "settings", to: "/settings", icon: SettingsIcon, label: "Settings", tid: "nav-settings", bucket: "secondary" },
  );
  return items;
}

export default function AppHeader() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const assoc     = user ? ASSOC_LOGO[user.association_id] : null;
  const tierColor = user ? (TIER_ACCENT[user.tier] || "#F5B82E") : "#F5B82E";
  const roleLabel = user ? formatRole(user.primary_role) : "";
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreWrap = useRef(null);

  // Show a persistent "← Dashboard" pill on every authed page EXCEPT
  // the dashboard itself and the anonymous landing pages.
  const showBackToDash = !!user &&
    !loc.pathname.startsWith("/dashboard") &&
    loc.pathname !== "/" &&
    loc.pathname !== "/login";

  // Close drawer + More popover on route change.
  useEffect(() => {
    setDrawerOpen(false);
    setMoreOpen(false);
  }, [loc.pathname]);

  // Dismiss More popover on outside-click.
  useEffect(() => {
    const h = (e) => {
      if (moreWrap.current && !moreWrap.current.contains(e.target)) {
        setMoreOpen(false);
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const navItems  = user ? buildNavItems(user) : [];
  const primary   = navItems.filter((n) => n.bucket === "primary");
  const secondary = navItems.filter((n) => n.bucket === "secondary");

  const doLogout = async () => {
    await logout();
    nav("/");
  };

  return (
    <header
      className="sticky top-0 z-40 border-b border-white/10 bg-[#050505]/85 backdrop-blur-xl"
      data-testid="app-header"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-3">
        {/* LEFT — Trade Org anchor + back pill */}
        <div className="flex items-center gap-3 min-w-0">
          {showBackToDash && (
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-white/15 text-[10px] tracking-[0.22em] uppercase text-white/75 hover:text-white hover:border-white/40 transition-colors"
              data-testid="header-back-to-dashboard"
              title="Back to main dashboard"
            >
              <ArrowLeft size={12}/> <span className="hidden sm:inline">Dashboard</span>
            </Link>
          )}
          {assoc ? (
            <div className="flex items-center gap-2.5" data-testid="header-trade-org">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center font-display font-bold text-xs text-white shrink-0"
                style={{ background: assoc.color, boxShadow: `0 0 0 2px ${assoc.color}40` }}
              >
                {assoc.label.slice(0, 2)}
              </div>
              <div className="leading-tight hidden 2xl:block">
                <div className="text-[10px] tracking-[0.2em] uppercase text-white/50">Trade Org</div>
                <div className="text-sm font-display font-semibold text-white" data-testid="header-trade-org-name">{assoc.label}</div>
              </div>
            </div>
          ) : (
            <Link
              to="/"
              className="group inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-white/55 hover:text-[#F5B82E] transition-colors"
              data-testid="header-home-link"
              title="Back to home"
            >
              <Home size={12} className="text-[#F5B82E]" />
              <span className="hidden md:inline">Verified Identity · Trusted Professionals</span>
              <span className="md:hidden font-semibold">Home</span>
            </Link>
          )}
        </div>

        {/* CENTER — Primary nav (authed, lg+ only) + More ▾ popover */}
        {user && (
          <nav className="hidden lg:flex items-center gap-1 text-sm mx-auto" data-testid="header-nav-primary">
            {primary.map((n) => (
              <NavItem key={n.key} to={n.to} icon={n.icon} label={n.label} tid={n.tid} />
            ))}
            {secondary.length > 0 && (
              <div className="relative" ref={moreWrap}>
                <button type="button"
                  onClick={() => setMoreOpen((v) => !v)}
                  data-testid="header-more-toggle"
                  className={`group flex items-center gap-2 px-3 py-2 rounded-md transition ${
                    moreOpen ? "text-white bg-white/[0.06]" : "text-white/60 hover:text-white hover:bg-white/5"
                  }`}
                >
                  <Menu size={15}/>
                  <span className="text-[12px] uppercase tracking-[0.14em]">More</span>
                  <ChevronDown size={12} className={`transition-transform ${moreOpen ? "rotate-180" : ""}`}/>
                </button>
                {moreOpen && (
                  <div className="absolute right-0 mt-2 w-56 rounded-xl border border-white/12 bg-[#0c0c0e] shadow-2xl overflow-hidden z-50"
                       data-testid="header-more-menu">
                    {secondary.map((n) => (
                      <Link key={n.key} to={n.to}
                        data-testid={n.tid}
                        className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-white/75 hover:text-white hover:bg-white/5 border-b border-white/5 last:border-0">
                        <n.icon size={15} className="text-white/45"/>
                        <span className="text-[12px] uppercase tracking-[0.14em]">{n.label}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}
          </nav>
        )}

        {/* CENTER — Public nav for logged-out visitors. */}
        {!user && (
          <nav className="hidden md:flex items-center gap-1 text-sm mx-auto" data-testid="header-public-nav">
            <NavItem to="/"             icon={Home}       label="Home"     tid="nav-public-home" />
            <NavItem to="/associations" icon={Users}      label="Unions"   tid="nav-public-unions" />
            <NavItem to="/onboard"      icon={IdCard}     label="Onboard"  tid="nav-public-onboard" />
            <NavItem to="/pricing"      icon={CreditCard} label="Pricing"  tid="nav-public-pricing" />
          </nav>
        )}

        {/* RIGHT — compact identity chip + brand + logout/register */}
        <div className="flex items-center gap-3 ml-auto">
          {user && <ContextSwitcher />}
          {user && roleLabel && (
            <div className="hidden 2xl:flex items-center gap-2.5" data-testid="header-primary-role">
              <div className="leading-tight text-right">
                <div className="text-[10px] tracking-[0.2em] uppercase text-white/50">Primary Role</div>
                <div className="text-sm font-display font-semibold text-white" data-testid="header-primary-role-label">{roleLabel}</div>
              </div>
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center"
                style={{ background: `${tierColor}22`, boxShadow: `0 0 0 2px ${tierColor}55` }}
              >
                <BadgeCheck size={16} style={{ color: tierColor }} />
              </div>
            </div>
          )}
          <Link to={user ? "/dashboard" : "/"} className="hidden sm:flex" data-testid="header-brand-link"
                title={user ? "Back to dashboard" : "Back to home"}>
            <BrandName variant="silver" size="sm" withClapper />
          </Link>
          {!user && (
            <div className="flex items-center gap-2" data-testid="header-public-auth">
              <Link to="/login"
                className="hidden sm:inline text-[10px] uppercase tracking-[0.24em] text-white/70 hover:text-white transition"
                data-testid="header-signin-link">
                Sign in
              </Link>
              <Link to="/register"
                className="text-[10px] uppercase tracking-[0.24em] px-4 py-2 rounded-full border border-white/25 hover:border-[#F5B82E] hover:text-[#F5B82E] transition-colors"
                data-testid="header-register-link">
                Create account
              </Link>
            </div>
          )}
          {user && (
            <>
              {/* Icon-only logout on md-lg to keep the strip inside the viewport */}
              <button
                onClick={doLogout}
                className="hidden lg:inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/70 hover:text-white transition"
                data-testid="logout-btn"
                title="Log out"
              >
                <LogOut size={14} /> <span className="hidden xl:inline">Logout</span>
              </button>
              {/* Below lg — hamburger opens the mobile drawer that
                  contains every nav item plus a big Logout row. */}
              <button
                onClick={() => setDrawerOpen(true)}
                className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-md hover:bg-white/5 text-white/80"
                data-testid="header-hamburger"
                aria-label="Open menu"
              >
                <Menu size={18}/>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Mobile drawer */}
      {user && drawerOpen && (
        <MobileDrawer
          items={navItems}
          user={user}
          roleLabel={roleLabel}
          tierColor={tierColor}
          onClose={() => setDrawerOpen(false)}
          onLogout={doLogout}
        />
      )}
    </header>
  );
}

function NavItem({ to, icon: Icon, label, tid }) {
  return (
    <Link
      to={to}
      data-testid={tid}
      className="group flex items-center gap-2 px-3 py-2 rounded-md text-white/60 hover:text-white hover:bg-white/5 transition"
    >
      <Icon size={15} className="group-hover:text-[color:var(--association-theme)]" />
      <span className="text-[12px] uppercase tracking-[0.14em]">{label}</span>
    </Link>
  );
}

function MobileDrawer({ items, user, roleLabel, tierColor, onClose, onLogout }) {
  return (
    <div className="lg:hidden fixed inset-0 z-50 bg-black/90 backdrop-blur-xl overflow-y-auto"
         data-testid="header-mobile-drawer">
      <div className="max-w-md mx-auto px-4 py-6 min-h-screen flex flex-col">
        <div className="flex items-center justify-between mb-8">
          <BrandName variant="gold" size="sm" withClapper />
          <button onClick={onClose}
            data-testid="header-mobile-drawer-close"
            className="w-10 h-10 rounded-md flex items-center justify-center hover:bg-white/5 text-white/80"
            aria-label="Close menu">
            <X size={20}/>
          </button>
        </div>

        {roleLabel && (
          <div className="flex items-center gap-3 rounded-xl border border-white/12 bg-white/[0.03] p-3 mb-6"
               data-testid="mobile-drawer-role">
            <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
                 style={{ background: `${tierColor}22`, boxShadow: `0 0 0 2px ${tierColor}55` }}>
              <BadgeCheck size={18} style={{ color: tierColor }} />
            </div>
            <div className="leading-tight">
              <div className="text-[10px] tracking-[0.22em] uppercase text-white/50">Primary Role</div>
              <div className="text-sm font-display font-semibold text-white">{roleLabel}</div>
              <div className="text-[10px] text-white/45 mt-0.5">{user.email}</div>
            </div>
          </div>
        )}

        <nav className="flex-1 space-y-1">
          {items.map((n) => (
            <Link key={n.key} to={n.to}
              data-testid={`mobile-nav-${n.key}`}
              onClick={onClose}
              className="flex items-center gap-3 rounded-lg px-3 py-3 text-white/75 hover:text-white hover:bg-white/[0.05] transition">
              <n.icon size={17} className="text-white/50 shrink-0"/>
              <span className="text-sm uppercase tracking-[0.18em]">{n.label}</span>
            </Link>
          ))}
        </nav>

        <button
          onClick={onLogout}
          data-testid="mobile-drawer-logout"
          className="mt-6 inline-flex items-center justify-center gap-2 h-12 rounded-full border border-white/20 hover:border-[#F5B82E] hover:text-[#F5B82E] text-sm uppercase tracking-[0.22em] font-semibold transition">
          <LogOut size={15}/> Log out
        </button>
      </div>
    </div>
  );
}
